/**
 * Which simulcast rung each tile actually needs, decided by how big it is drawn.
 *
 * We publish three sizes of every camera and, until this existed, asked for
 * none of them — so mediasoup handed every viewer the largest rung their
 * bandwidth could carry, whether the tile was full-screen or a thumbnail in a
 * grid of sixteen. The layers were paid for and thrown away.
 *
 * Shaped after `rtp-stats.ts` on purpose, and for the same reason it gives:
 * a tile knows who it is showing and which source, not which consumer is
 * carrying it. So tiles report sizes against the `statsKey` triple and
 * `useConsumers` — the only place that holds the consumerId — translates a
 * preference into an actual SFU call. Nothing about transports leaks into a
 * component whose job is to draw a rectangle.
 *
 * Ported from edumeet's ResolutionWatcher/ResolutionReporter pair
 * (`edumeet-client/src/utils/resolutionWatcher.tsx`).
 */
import { statsKey, tileStatsTarget, type StatsTile } from './rtp-stats'

/** Rungs are numbered low to high; mediasoup clamps anything out of range. */
export type PreferredLayers = { spatialLayer: number; temporalLayer: number }

/**
 * The canonical key. `useConsumers` subscribes with exactly this, built from
 * the producer's peerId and sourceType.
 */
export function layerKey(peerId: string, sourceType: string): string {
  return statsKey(peerId, sourceType, 'video')
}

/**
 * The same key, derived from what a tile knows about itself.
 *
 * Lives here rather than in the component so the two halves cannot drift: a key
 * that disagrees by one character is not an error, it is silence — the tile
 * reports sizes forever and nobody is listening.
 *
 * The additional-camera branch is the one that bites. An extra camera's tile id
 * is `${ownerId}::${sourceId}` and it carries no `sfuPeerId`, so the obvious
 * `sfuPeerId ?? id` produces `"<peer>::aux1:aux1:video"` while the consumer is
 * registered under `"<peer>:aux1:video"`. The consumer belongs to the OWNER,
 * under the source slot.
 */
export function layerKeyForTile(p: StatsTile): string | null {
  // A local preview has no consumer to ask, so there is nothing to prefer.
  if (p.isLocal) return null
  const t = tileStatsTarget(p)
  return t ? layerKey(t.peer, t.sourceType) : null
}

type Entry = {
  /** Width reported by each live renderer of this source, keyed by reporter id. */
  sizes: Map<string, number>
  /** Last layer we told anyone about, so we only emit on a real change. */
  last: PreferredLayers | null
  /** A resize-driven downgrade waiting for the size to stop changing. */
  pendingDowngrade: ReturnType<typeof setTimeout> | null
}

/**
 * How long a tile must hold a smaller size before it is served a lower rung.
 *
 * Tiles resize in motion — a layout glide when someone joins, a side panel
 * opening, the rail being dragged. Following every intermediate width switched
 * every visible camera to a lower rung at the moment the grid moved, which on
 * screen is a room-wide blur exactly when attention is on the grid. Restarted
 * by every new report, so it measures stillness, not time since the first one.
 */
const DOWNGRADE_SETTLE_MS = 800

/**
 * A tile within this fraction below a rung's threshold keeps that rung. Without
 * a band, a width sitting on 480 or 960 flips rungs on every pixel of jitter.
 */
const DOWNGRADE_HYSTERESIS = 0.1

/** Smallest width that justifies each rung — the inverse of `layerForWidth`. */
function rungFloor(spatialLayer: number): number {
  return spatialLayer >= 2 ? 960 : spatialLayer === 1 ? 480 : 0
}

const entries = new Map<string, Entry>()
const listeners = new Map<string, Set<(layers: PreferredLayers) => void>>()

/**
 * Rendered width to spatial rung.
 *
 * The thresholds are edumeet's (`mediaService.tsx:577`). Temporal is always the
 * top rung: dropping frame rate is a worse trade than dropping resolution for
 * anything a person is looking at, and mediasoup clamps the request anyway.
 *
 * Deliberately coarse — three buckets, so a tile drifting a few pixels while a
 * panel animates cannot produce a stream of layer changes.
 */
export function layerForWidth(width: number): PreferredLayers {
  const spatialLayer = width >= 960 ? 2 : width >= 480 ? 1 : 0
  return { spatialLayer, temporalLayer: 2 }
}

function emit(key: string, entry: Entry, next: PreferredLayers): void {
  entry.last = next
  listeners.get(key)?.forEach((fn) => fn(next))
}

function cancelPendingDowngrade(entry: Entry): void {
  if (entry.pendingDowngrade) {
    clearTimeout(entry.pendingDowngrade)
    entry.pendingDowngrade = null
  }
}

/**
 * Decide the rung for a source.
 *
 * - `report`: a renderer's size changed. Upgrades and the first answer apply at
 *   once — a tile that just got bigger should sharpen now. A downgrade is held
 *   inside the hysteresis band and otherwise waits for the size to settle.
 * - `settled`: the size has been still long enough; apply what it now needs.
 * - `drop`: a renderer went away. Not a wobble — the remaining renderers decide
 *   immediately, so closing a big view stops paying for its rung.
 */
function recompute(key: string, reason: 'report' | 'settled' | 'drop'): void {
  const entry = entries.get(key)
  if (!entry) return

  let widest = 0
  for (const w of entry.sizes.values()) if (w > widest) widest = w
  if (widest === 0) return

  const next = layerForWidth(widest)
  const last = entry.last
  if (last && last.spatialLayer === next.spatialLayer && last.temporalLayer === next.temporalLayer) {
    cancelPendingDowngrade(entry)
    return
  }

  const isDowngrade = last !== null && next.spatialLayer < last.spatialLayer
  if (!isDowngrade || reason === 'drop') {
    cancelPendingDowngrade(entry)
    emit(key, entry, next)
    return
  }

  // Close enough to the current rung's threshold: keep it.
  if (widest >= rungFloor(last.spatialLayer) * (1 - DOWNGRADE_HYSTERESIS)) {
    cancelPendingDowngrade(entry)
    return
  }

  if (reason === 'settled') {
    emit(key, entry, next)
    return
  }

  cancelPendingDowngrade(entry)
  entry.pendingDowngrade = setTimeout(() => {
    entry.pendingDowngrade = null
    recompute(key, 'settled')
  }, DOWNGRADE_SETTLE_MS)
}

/**
 * Report the rendered width of one `<video>` showing `key`.
 *
 * `reporterId` must be stable for the lifetime of that element and unique among
 * concurrent renderers of the same source — otherwise two views collapse into
 * one entry and the max is wrong.
 */
export function reportSize(key: string, reporterId: string, width: number): void {
  let entry = entries.get(key)
  if (!entry) {
    entry = { sizes: new Map(), last: null, pendingDowngrade: null }
    entries.set(key, entry)
  }
  if (entry.sizes.get(reporterId) === width) return
  entry.sizes.set(reporterId, width)
  recompute(key, 'report')
}

/** A renderer went away. The remaining ones decide the rung between them. */
export function dropReporter(key: string, reporterId: string): void {
  const entry = entries.get(key)
  if (!entry || !entry.sizes.delete(reporterId)) return
  if (entry.sizes.size === 0) {
    // Nothing left to measure, so nothing left to settle.
    cancelPendingDowngrade(entry)
    // Keep `last` — the consumer is still alive and still on whatever rung we
    // last asked for. Re-emitting when a tile remounts would be noise.
    return
  }
  recompute(key, 'drop')
}

/**
 * Called by whoever can turn a preference into an SFU request. Fires on change
 * only; if a preference already exists the current value is delivered at once,
 * because a consumer created after its tile was measured would otherwise sit on
 * the default top rung until the next resize.
 */
export function subscribe(key: string, listener: (layers: PreferredLayers) => void): () => void {
  let set = listeners.get(key)
  if (!set) {
    set = new Set()
    listeners.set(key, set)
  }
  set.add(listener)

  const current = entries.get(key)?.last
  if (current) listener(current)

  return () => {
    const s = listeners.get(key)
    if (!s) return
    s.delete(listener)
    if (s.size === 0) listeners.delete(key)
  }
}

/** Forget a source entirely. Call when its consumer closes. */
export function release(key: string): void {
  const entry = entries.get(key)
  if (entry) cancelPendingDowngrade(entry)
  entries.delete(key)
  listeners.delete(key)
}

/** Test seam. */
export const __DOWNGRADE_SETTLE_MS = DOWNGRADE_SETTLE_MS

/** Test seam. */
export function __reset(): void {
  entries.forEach(cancelPendingDowngrade)
  entries.clear()
  listeners.clear()
}
